import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Editor } from '@tiptap/core'
import { cn } from '@/lib/utils'

type OutlineItem = { pos: number; level: number; text: string }

function readHeadings(editor: Editor): Array<OutlineItem> {
  const items: Array<OutlineItem> = []
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'heading') {
      const text = node.textContent.trim()
      if (text) items.push({ pos, level: Number(node.attrs.level) || 1, text })
      return false
    }
    return true
  })
  return items
}

/**
 * The outline rail beside a doc (design 3b): its headings, live, click to
 * jump. Hidden until the doc has at least two headings.
 */
export function DocOutline({ editor }: { editor: Editor | null }) {
  const { t } = useTranslation('projects')
  const [items, setItems] = useState<Array<OutlineItem>>([])

  useEffect(() => {
    if (!editor) return
    const update = () => setItems(readHeadings(editor))
    update()
    editor.on('update', update)
    return () => {
      editor.off('update', update)
    }
  }, [editor])

  if (!editor || items.length < 2) return null
  const minLevel = Math.min(...items.map((i) => i.level))

  return (
    <nav aria-label={t('docs.outline')} className="text-[12.5px]">
      <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {t('docs.outline')}
      </p>
      <ul className="space-y-0.5 border-l">
        {items.map((item) => (
          <li key={item.pos}>
            <button
              type="button"
              onClick={() => {
                const dom = editor.view.nodeDOM(item.pos)
                if (dom instanceof HTMLElement) dom.scrollIntoView({ behavior: 'smooth', block: 'start' })
                editor.commands.setTextSelection(item.pos + 1)
              }}
              className={cn(
                '-ml-px block w-full truncate border-l border-transparent py-1 pr-2 text-left text-muted-foreground transition-colors hover:border-foreground hover:text-foreground',
              )}
              style={{ paddingLeft: `${0.75 + (item.level - minLevel) * 0.75}rem` }}
            >
              {item.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  )
}
